import React, { useEffect, useState, useCallback } from 'react';
import { View, ScrollView, TouchableOpacity, RefreshControl } from 'react-native';
import { observer } from 'mobx-react';
import { RewardedAdEventType, AdEventType } from 'react-native-google-mobile-ads';

import Modal from './shared/Modal';
import Text from './shared/Text';
import IconButton from './shared/IconButton';
import Button from './shared/Button';
import { SmallSlot } from './common/ThingItem';
import DroppedModal from './DroppedModal';
import heroStore from '../stores/hero';
import appStore from '../stores/app';
import adsStore from '../lib/ads';
import { getWarriorBonuses, getBonusesCatalog, onWarriorAdViewsChanged } from '../lib/dbCalls';
import { settledBonus, awardAdView } from '../lib/apiCalls';
import { AD_VIEWS_DAILY_CAP } from '../lib/config';
import { action, getLocalValue, t } from '../utils/appUtils';
import { getUtcDayKey } from '../utils/utils';
import { isQuestCondition } from '../utils/questUtils';
import { IS_WEB } from '../lib/appConstants';
import {
  BASE_COLOR,
  GOLDEN100,
  MARGIN_5,
  MARGIN_10,
  MARGIN_15,
  MARGIN_25,
} from './styleConstants';
import { WarriorBonusType, BonusToType, WarriorAdViewsType } from '../lib/types';

type Props = { onHide: () => void };

type AnnotatedBonus = WarriorBonusType & { _originalIndex: number };

const Cell: React.FC<{ flex: number; bold?: boolean; children?: any }> = ({
  flex,
  bold,
  children,
}) => (
  <View style={{ flex, paddingHorizontal: 4 }}>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Text bold={bold}>{String(children)}</Text>
    ) : (
      children
    )}
  </View>
);

const Row: React.FC<{ header?: boolean; children?: any }> = ({ header, children }) => (
  <View
    style={{
      flexDirection: 'row',
      paddingVertical: 4,
      borderBottomWidth: header ? 1 : 0,
      borderBottomColor: GOLDEN100,
      marginBottom: header ? MARGIN_5 : 0,
    }}
  >
    {children}
  </View>
);

const BonusesModal = observer(function BonusesModal({ onHide }: Props) {
  const [bonuses, setBonuses] = useState<AnnotatedBonus[]>([]);
  const [catalog, setCatalog] = useState<BonusToType[]>([]);
  const [fetching, setFetching] = useState(true);
  const [openedChest, setOpenedChest] = useState<AnnotatedBonus | null>(null);
  const [adViews, setAdViews] = useState<WarriorAdViewsType | null>(null);
  const [adLoading, setAdLoading] = useState(false);

  const fetch = useCallback(async () => {
    setFetching(true);
    const [catalogResult, bonusesResult] = await Promise.all([
      getBonusesCatalog(),
      getWarriorBonuses(heroStore.hero.id),
    ]);
    const annotated: AnnotatedBonus[] = bonusesResult.map((b, i) => ({ ...b, _originalIndex: i }));
    setCatalog(catalogResult);
    // Quest rewards share the warriorBonuses store but are claimed from the
    // Quests screen, so keep them out of the classic bonuses list.
    setBonuses(annotated.filter((b) => !b.settled && !isQuestCondition(b.condition)));
    setFetching(false);
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const ref = onWarriorAdViewsChanged(heroStore.hero.id, (snap: any) => {      
      setAdViews(snap.val() || null);
    });
    return () => {
      (ref as any).off();
    };
  }, []);

  const handleChestClick = (entry: AnnotatedBonus) => {
    setOpenedChest(entry);
    setBonuses((prev) => prev.filter((b) => b !== entry));
    settledBonus(entry._originalIndex);
  };

  const today = getUtcDayKey(Date.now());
  const todayCount = adViews && adViews.dayKey === today ? adViews.count : 0;
  const atCap = todayCount >= AD_VIEWS_DAILY_CAP;
  const showFreeBonusRow = !IS_WEB && adsStore.adsReady;

  const onWatchAd = () => {
    setAdLoading(true);
    const ad = adsStore.loadRewarded();

    const subLoaded = ad.addAdEventListener(RewardedAdEventType.LOADED, () => {
      ad.show();
    });
    const subEarned = ad.addAdEventListener(
      RewardedAdEventType.EARNED_REWARD,
      async () => {
        try {
          await awardAdView();
          await fetch();
          appStore.showMessage({ type: 'success', text: t('ads.rewardCredited') });
        } catch (e) {
          appStore.showMessage({ type: 'error', text: t('somethingWrongError') });
        }
      },
    );
    const subFailed = ad.addAdEventListener(AdEventType.ERROR, () => {
      appStore.showMessage({ type: 'error', text: t('ads.notAvailable') });
      setAdLoading(false);
    });
    const subClosed = ad.addAdEventListener(AdEventType.CLOSED, () => {
      setAdLoading(false);
      adsStore.consumeRewarded();
      subLoaded();
      subEarned();
      subFailed();
      subClosed();
    });
  };

  return (
    <Modal title={t('bonuses.title')} onHide={onHide}>
      <View style={{ marginTop: 40, marginBottom: 25, flex: 1 }}>
        <ScrollView
          refreshControl={
            <RefreshControl
              tintColor={BASE_COLOR}
              refreshing={fetching}
              onRefresh={() => action(fetch)}
            />
          }
        >
          {showFreeBonusRow ? (
            <View
              style={{
                marginLeft: MARGIN_25,
                marginRight: MARGIN_25,
                marginBottom: MARGIN_15,
                paddingBottom: MARGIN_10,
                borderBottomWidth: 1,
                borderBottomColor: GOLDEN100,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text bold style={{ flex: 1 }}>
                  {t('ads.watchForBonus')}
                </Text>
                {atCap ? (
                  <Text style={{ fontStyle: 'italic' }}>{t('ads.dailyCapReached')}</Text>
                ) : (
                  <Button onPress={onWatchAd} disabled={adLoading}>
                    {t('ads.freeBonus')}
                  </Button>
                )}
              </View>
              {!atCap ? (
                <Text style={{ fontSize: 11, marginTop: 4 }}>
                  {t('ads.todayCount', {
                    count: AD_VIEWS_DAILY_CAP - todayCount,
                    cap: AD_VIEWS_DAILY_CAP,
                  })}
                </Text>
              ) : null}
            </View>
          ) : null}

          <View style={{ marginLeft: MARGIN_25, marginRight: MARGIN_25 }}>
            <Row header>
              <Cell flex={1} bold>
                {t('bonuses.columns.name')}
              </Cell>
              <Cell flex={2} bold>
                {t('bonuses.columns.description')}
              </Cell>
            </Row>
            {catalog.map((c) => (
              <Row key={c.id}>
                <Cell flex={1} bold>
                  {getLocalValue(c, 'label')}
                </Cell>
                <Cell flex={2}>{getLocalValue(c, 'description')}</Cell>
              </Row>
            ))}
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginLeft: MARGIN_25,
              marginTop: MARGIN_15,
              marginBottom: MARGIN_10,
            }}
          >
            <Text bold>{t('bonuses.chestsHeading')}</Text>
            {IS_WEB ? (
              <IconButton
                onPress={() => action(fetch)}
                style={{ marginLeft: MARGIN_10 }}
                size={12}
                title={t('refresh')}
                source={require('../assets/images/refresh.png')}
                iconStyle={{ width: 20, height: 20 }}
                name="refresh"
              />
            ) : null}
          </View>
          {bonuses.length === 0 ? (
            !fetching ? (
              <Text style={{ marginLeft: MARGIN_25 }}>{t('bonuses.noRewards')}</Text>
            ) : null
          ) : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginLeft: MARGIN_25 }}>
              {bonuses.map((entry) => (
                <TouchableOpacity
                  key={`${entry.condition}-${entry.earnedAt}`}
                  onPress={() => handleChestClick(entry)}
                  style={{ marginRight: MARGIN_10, marginBottom: MARGIN_10 }}
                >
                  <SmallSlot iconSizeP={0.9} icon={require('../assets/images/chest.png')} />
                </TouchableOpacity>
              ))}
            </View>
          )}
        </ScrollView>
      </View>

      {openedChest ? (
        <DroppedModal bonus={openedChest.bonus} onHide={() => setOpenedChest(null)} />
      ) : null}
    </Modal>
  );
});

export default BonusesModal;
